import { createContext, useContext, useRef, useState, useCallback, useEffect, FC, useMemo } from 'react'
import { ChildrenType } from '../interfaceTypes'
import {
  Entry,
  EntryStatus,
  useGetRunningTimersLazyQuery,
  usePauseOrResumeTimerMutation,
  useStartTimerMutation,
  useStopTimerMutation
} from '../../generated'
import { Alert } from '../components/common/Alert'
import { HELMER_KEYS } from '../constants'
import { TimerForm } from '../interfaceTypes/forms'
import { deleteTimerKeys, persistTimerState } from '../utils'
import { EntryContext } from './EntryContext'
import { EntryActionType } from '../components/entries/EntryReducer'
import { AuthContext } from './AuthContext'

// --- split into two contexts
type TimerState = {
  isRunning: boolean
  elapsedTime: number
  startTime: number | null
  formValues: TimerForm | null
}

type TimerControls = {
  startTimer: (data?: TimerForm) => void
  pauseTimer: (entryId: string) => void
  resetTimer: (entryId?: string) => Promise<void>
  closeOrDiscard: () => void
}

interface TimerStorage {
  startTime: number
  elapsedTime: number
  isPaused: boolean
}

const TimerStateContext = createContext<TimerState | null>(null)
const TimerControlsContext = createContext<TimerControls | null>(null)

// --- Provider
const TimerProvider: FC<ChildrenType> = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [startTime, setStartTime] = useState<number | null>(null)
  const [formValues, setFormValues] = useState<TimerForm | null>(null)

  const { dispatch } = useContext(EntryContext)
  const { isLoggedIn, isAdmin } = useContext(AuthContext)

  // The control callbacks below are memoised with an empty dependency list so
  // their identity is stable for the lifetime of the provider. To read the
  // *current* state without going stale, they read from these refs instead of
  // from the closed-over state variables. This is the fix for the bug where a
  // paused timer restarted from zero: the frozen callbacks were seeing the
  // first render's `isRunning=false` / `elapsedTime=0` forever.
  const isRunningRef = useRef(isRunning)
  const elapsedTimeRef = useRef(elapsedTime)
  const startTimeRef = useRef(startTime)
  const formValuesRef = useRef(formValues)

  useEffect(() => {
    isRunningRef.current = isRunning
  }, [isRunning])
  useEffect(() => {
    elapsedTimeRef.current = elapsedTime
  }, [elapsedTime])
  useEffect(() => {
    startTimeRef.current = startTime
  }, [startTime])
  useEffect(() => {
    formValuesRef.current = formValues
  }, [formValues])

  // Tear down every trace of the timer: clock, the entry it was attached to and
  // the persisted keys. `formValues`/`currentEntry` MUST be cleared too — they
  // hold the entry id, and leaving them behind made the dialog reopen with the
  // discarded entry still filled in, so a second Discard hit the server with an
  // id that no longer exists ("Entry with ID … not found").
  const closeOrDiscard = useCallback(() => {
    // Refs first, for the same reason as above: a follow-up handler in the same
    // event must not see this timer as still live.
    isRunningRef.current = false
    startTimeRef.current = null
    elapsedTimeRef.current = 0

    setIsRunning(false)
    setElapsedTime(0)
    setStartTime(null)
    setFormValues(null)
    dispatch({ type: EntryActionType.SET_CURRENT_ENTRY, currentEntry: null })
    deleteTimerKeys()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [startTimerFn] = useStartTimerMutation({
    // `startTimer` below anchors the clock and writes localStorage optimistically,
    // so a failed start used to leave a clock ticking against no server entry —
    // with an empty entryId, which then failed again on save and lost the time
    // silently (this handler only did a console.log). Surface it and roll the
    // local timer back so what the user sees matches what was recorded.
    onError: error => {
      Alert.error(`Timer could not be started: ${error.message}`)
      closeOrDiscard()
    },
    onCompleted: data => {
      const { startTimer } = data ?? {}
      const { entry } = startTimer ?? {}
      const formData = Object.entries(entry || {}).reduce((acc, [key, value]) => {
        const transformedKey = key === 'id' ? 'entryId' : key
        return {
          ...acc,
          [transformedKey]: value
        }
      }, {} as TimerForm)
      setFormValues(formData)

      dispatch({
        type: EntryActionType.SET_CURRENT_ENTRY,
        // Cast to Entry: the timer's form-level shape (and the query's partial
        // `case`) is an approximation of the full Entry — same pattern used in
        // getRunningTimers.onCompleted below.
        currentEntry: {
          ...formData,
          id: entry?.id ?? '',
          createdAt: entry?.createdAt ?? '',
          updatedAt: entry?.updatedAt ?? '',
          status: entry?.status ?? EntryStatus.Unbilled
        } as Entry
      })
      localStorage.setItem(HELMER_KEYS.FORM, JSON.stringify(formData))
    }
  })
  const [stopTimerFn] = useStopTimerMutation({
    // Was a console.log, so a failed stop looked identical to a successful one:
    // the local timer is cleared before the mutation is awaited (see resetTimer),
    // so the clock vanished and nothing said the time had not been saved.
    onError: error =>
      Alert.error(`Timer could not be stopped and your time was not saved: ${error.message}`),
    // Deliberately NOT a toast. Stopping the clock is one step of saving the
    // entry, not an outcome the user needs told: <Timer> follows this with
    // updateEntry, whose "Time entry saved successfully" is the message that
    // actually reports the result. Surfacing both meant two notifications for a
    // single Save. Failures still toast — see onError above.
    onCompleted: data => {
      const { stopTimer } = data ?? {}
      const { response } = stopTimer ?? {}
      const { message } = response ?? {}
      console.log('Timer stopped:', message ?? '')
    }
  })
  const [pauseOrResumeTimer] = usePauseOrResumeTimerMutation()
  const [getRunningTimers] = useGetRunningTimersLazyQuery({
    onCompleted: data => {
      // Local state wins over the server snapshot – if we already have a
      // persisted timer, don't clobber it.
      if (localStorage.getItem(HELMER_KEYS.TIMER)) return

      const { entries } = data?.getRunningTimers ?? {}
      if (!entries?.length) return

      const entry = entries[0]
      const { isTimerPaused, startTime: existingStartTime, totalPausedTime = 0, pauseTime } = entry || {}

      // Elapsed time excludes any paused periods.
      let elapsed = 0
      if (existingStartTime) {
        const end = isTimerPaused && pauseTime ? Date.parse(pauseTime) : Date.now()
        elapsed = end - Date.parse(existingStartTime) - (totalPausedTime || 0)
      }

      // Re-anchor the start so the invariant `now - startTime === elapsed`
      // holds for the tick loop below.
      const anchoredStart = Date.now() - elapsed
      setStartTime(anchoredStart)
      setIsRunning(!isTimerPaused)
      setElapsedTime(elapsed)

      setFormValues({ ...entry, entryId: entry?.id || '' } as TimerForm)
      dispatch({
        type: EntryActionType.SET_CURRENT_ENTRY,
        currentEntry: {
          ...entry,
          id: entry?.id ?? ''
        } as Entry
      })

      persistTimerState({ startTime: anchoredStart, isPaused: !!isTimerPaused, elapsedTime: elapsed })
    }
  })

  // Start a fresh timer or resume a paused one. Identity-stable ([] deps);
  // reads live state via refs.
  const startTimer = useCallback(
    (data?: TimerForm) => {
      if (isRunningRef.current) return // already running – nothing to do

      const now = Date.now()
      const previousElapsed = elapsedTimeRef.current || 0
      // Anchor the start point so `now - startTime` always equals total elapsed.
      const anchoredStart = now - previousElapsed
      const isFreshTimer = startTimeRef.current === null

      // Claim the refs synchronously. They are normally synced by an effect,
      // which does not run until after the click has finished propagating — so
      // two handlers firing inside one event would both pass the guard above
      // and send two toggles. Writing them here closes that window.
      isRunningRef.current = true
      startTimeRef.current = anchoredStart

      if (isFreshTimer) {
        // No existing entry yet – create one on the server. Strip UI-only
        // helper fields (entryId/duration/caseNumber/case) that are NOT part of
        // StartTimerInput — sending them makes the mutation fail with an
        // "unknown field" error and the timer never persists. `rateType` and
        // `sendNotesToMatterNotes` ARE part of the input and must be kept.
        const { entryId, duration, caseNumber, case: _case, ...rest } = data || {}
        startTimerFn({ variables: { input: { ...rest } } })
        setFormValues(prev => ({ ...prev, ...data } as TimerForm))
      } else {
        // Existing paused entry – toggle it back to running.
        const timeEntryId = data?.entryId || formValuesRef.current?.entryId || ''
        pauseOrResumeTimer({ variables: { timeEntryId } })
      }

      setStartTime(anchoredStart)
      setIsRunning(true)
      persistTimerState({ startTime: anchoredStart, isPaused: false, elapsedTime: previousElapsed })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // Pause the timer while preserving accumulated time so resume continues
  // seamlessly. We deliberately DO NOT remove the TIMER key here – doing so was
  // what made resume lose all elapsed time and restart from zero.
  const pauseTimer = useCallback((entryId: string) => {
    // `pauseOrResumeTimer` is a server-side *toggle*, so pausing a timer that
    // is already paused silently RESUMES it on the backend while the UI keeps
    // showing paused. The entry then accrues time nobody can see and saves
    // longer than the clock ever displayed. Mirror the guard in startTimer,
    // and claim the ref synchronously so it also holds for two handlers firing
    // within a single click.
    if (!isRunningRef.current) return
    isRunningRef.current = false

    // Freeze the clock at the *exact* instant of the click.
    //
    // `elapsedTimeRef` only refreshes on the 1s tick, so it can be up to a
    // second behind real time. The server, meanwhile, measures the pause from
    // the moment the request lands. Freezing the stale value therefore threw
    // away a fraction of a second of genuine running time on every pause, and
    // resume re-anchored from that short value — so the UI clock fell further
    // behind the server with each pause/resume cycle, and the saved entry came
    // out longer than the clock ever showed.
    const exactElapsed =
      startTimeRef.current !== null ? Date.now() - startTimeRef.current : elapsedTimeRef.current || 0
    elapsedTimeRef.current = exactElapsed
    setElapsedTime(exactElapsed)

    setIsRunning(false)
    pauseOrResumeTimer({ variables: { timeEntryId: entryId } })
    persistTimerState({
      startTime: startTimeRef.current ?? Date.now(),
      isPaused: true,
      elapsedTime: exactElapsed
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Stop and discard the timer entirely. Awaits the stop mutation so callers
  // can safely refresh the page once the entry is persisted.
  const resetTimer = useCallback(
    async (entryId?: string) => {
      // Clears state and the persisted keys before we await, so a failing stop
      // can't leave a half-live timer behind.
      closeOrDiscard()
      try {
        await stopTimerFn({ variables: { timeEntryId: entryId || '' } })
      } catch {
        // Error is already surfaced by the mutation's onError handler; still
        // fall through to clear local timer state.
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeOrDiscard]
  )

  // Tick once per second while running. A single interval keyed on
  // [isRunning, startTime] replaces the old requestAnimationFrame loop, which
  // re-rendered ~60x/second, froze in background tabs, and could leak duplicate
  // loops through a shared ref.
  useEffect(() => {
    if (!isRunning || startTime === null) return

    setElapsedTime(Date.now() - startTime) // update immediately, don't wait a second
    const intervalId = setInterval(() => {
      setElapsedTime(Date.now() - startTime)
    }, 1000)

    return () => clearInterval(intervalId)
  }, [isRunning, startTime])

  // Restore timer state on mount and whenever auth state resolves. Persisted
  // local state takes priority; otherwise fall back to the server's running
  // timers once the user is logged in.
  useEffect(() => {
    const stored = localStorage.getItem(HELMER_KEYS.TIMER)

    if (stored) {
      const timer = JSON.parse(stored) as TimerStorage
      setStartTime(timer.startTime)
      setIsRunning(!timer.isPaused)
      // While running, recompute from the anchor so we account for time that
      // passed since it was persisted; while paused, keep the frozen value.
      setElapsedTime(timer.isPaused ? timer.elapsedTime : Date.now() - timer.startTime)

      const formStored = localStorage.getItem(HELMER_KEYS.FORM)
      if (formStored) {
        const parsed = JSON.parse(formStored)
        setFormValues(parsed)
        dispatch({
          type: EntryActionType.SET_CURRENT_ENTRY,
          currentEntry: {
            ...parsed
          } as Entry
        })
      }
    } else if (isLoggedIn && isAdmin) {
      // The Clock is admin-only and the server now rejects these mutations for
      // everyone else, so only admins have a server-side running timer to
      // restore. Calling this for a non-admin would just 403 on every login.
      getRunningTimers({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isAdmin])

  // --- split values ---
  const stateValue = useMemo(
    () => ({ isRunning, elapsedTime, startTime, formValues }),
    [isRunning, elapsedTime, startTime, formValues]
  )

  // Safe to memoise: every control is identity-stable, so this object never
  // needs to change – and consumers never receive a stale callback.
  const controlsValue = useMemo(
    () => ({ startTimer, pauseTimer, resetTimer, closeOrDiscard }),
    [startTimer, pauseTimer, resetTimer, closeOrDiscard]
  )

  return (
    <TimerControlsContext.Provider value={controlsValue}>
      <TimerStateContext.Provider value={stateValue}>{children}</TimerStateContext.Provider>
    </TimerControlsContext.Provider>
  )
}

// --- hooks
export const useTimer = () => {
  const state = useContext(TimerStateContext)
  const controls = useContext(TimerControlsContext)
  if (!state || !controls) {
    throw new Error('useTimer must be used within a TimerProvider')
  }
  return { ...state, ...controls } // full API, old consumers unchanged
}

export const useTimerControls = () => {
  const controls = useContext(TimerControlsContext)
  if (!controls) {
    throw new Error('useTimerControls must be used within a TimerProvider')
  }
  return controls
}

export default TimerProvider
